import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDownRounded'
import { makeStyles } from '@material-ui/styles'
import { Button, Menu, MenuItem } from '@mui/material'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { getIndianStates, topUpSubscription, membershipActions } from 'api'
import BaseEntity from 'app/components/BaseEntity'
import ModalForm from 'app/components/ModalForm'
import { ParentDetails } from 'app/components/shared/ParentDetails'
import {
  user_statuses,
  commonAttributes,
  nonMemberActions,
  parentActions,
  tableColumnDefaultOption
} from 'app/config'
import constants from 'app/constants'
import { eventAlert, eventEmitter } from 'app/helpers'
import { fetchParentData, updateUserData } from 'app/store/actions/userEntity'
import { parentDataTransformer } from 'app/transformer'
import { validateNotEmpty } from 'app/validators'

import GuardiansPanel from './components/GuardiansPanel'
import ParentAvatarPanel from './components/ParentAvatarPanel'
import { columns } from './config'
import MasterForm from './MasterForm'
import './styles.css'

const Parent = () => {
  const rawParentData = useSelector(
    (state) => state.userEntityReducer.parentData
  )

  // Multi-guardian: render one row per active guardian instead of one row
  // per family. Each row carries the parent's full data (so existing column
  // accessors / actions keep working) but the user's first_name/last_name/
  // phone_number are overridden with the guardian's, so the table actually
  // shows Rohan, Asha, Lakshmi, … as distinct rows.
  //
  // Pagination caveat: the backend paginates by family. After flattening,
  // a page of 25 families surfaces as 25-75 guardian rows. Acceptable for v1.
  const parentData = React.useMemo(() => {
    if (!rawParentData?.data) return rawParentData
    const flat = rawParentData.data.flatMap((parent) => {
      const guardians = parent?.guardians || []
      // THE ACCOUNT HOLDER IS ALWAYS A ROW (owner, 2026-10-15). This used to
      // emit the family's own row only when it had NO guardians, so adding one
      // guardian made the account holder vanish: searching your own number
      // returned your family, and the single row it drew was your uncle, with
      // his name and his phone. The account exists; the list simply stopped
      // drawing it.
      const own = {
        ...parent,
        _guardianKey: parent.user_id || parent?.user?.uuid,
        _relationship: parent.relationship_with_student || '—',
        _is_primary: true
      }
      if (!guardians.length) {
        return [own]
      }
      return [
        own,
        ...guardians.map((g) => ({
          ...parent,
          // Overlay guardian identity on the family User. uuid stays the
          // family User UUID so click → family profile works.
          user: {
            ...parent.user,
            first_name: g.first_name || parent.user?.first_name,
            last_name: g.last_name || parent.user?.last_name,
            phone_number: g.phone_number || parent.user?.phone_number
          },
          _guardianKey: `${parent.user_id || parent?.user?.uuid}-${g.uuid}`,
          _guardian: g,
          _relationship: g.relationship_display || g.relationship || '—',
          _is_primary: g.is_primary_outbound
        }))
      ]
    })
    // Keep backend count/next/previous — pagination is by family, not guardian row.
    return { ...rawParentData, data: flat }
  }, [rawParentData])

  const [states, setStates] = React.useState({})
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const [action, setAction] = React.useState('')
  const [editIndex, setEditIndex] = React.useState(null)
  const dispatch = useDispatch()
  const [anchorEl, setAnchorEl] = React.useState([])
  const [profileDetailsModalOpen, setProfileDetailsModalOpen] =
    React.useState(false)
  const [parentDetails, setParentDetails] = React.useState([])
  // Tracks the currently-selected parent's UUID for the Guardians panel
  // embedded inside the profile-details modal (multi-guardian feature, M6).
  const [selectedParentUuid, setSelectedParentUuid] = React.useState(null)
  const [selectedParentImage, setSelectedParentImage] = React.useState('')
  const handleClick = (event, id) => {
    let anchorEls = [...anchorEl]
    anchorEls[id] = event.target
    setAnchorEl(anchorEls)
  }
  const handleClose = (id) => {
    let anchorEls = [...anchorEl]
    anchorEls[id] = null
    setAnchorEl(anchorEls)
  }

  const profileButtonOnclickHandler = async (dataIndex) => {
    const parentDetailsData = await parentDataTransformer(
      parentData.data[dataIndex]
    )
    setParentDetails(parentDetailsData)
    // The parent row in our list has user.uuid as the family identity
    // (Parent OneToOne with User). Pass it to the Guardians panel.
    setSelectedParentUuid(
      parentData?.data?.[dataIndex]?.user?.uuid ||
        parentData?.data?.[dataIndex]?.user_id ||
        null
    )
    setSelectedParentImage(parentData?.data?.[dataIndex]?.user?.image || '')
    setProfileDetailsModalOpen(true)
  }

  React.useEffect(() => {
    ;(async () => {
      const { data: indianStates } = await getIndianStates()
      const statesMap = {}
      indianStates.forEach((state) => {
        statesMap[state[0]] = state[1]
      })
      setStates(statesMap)
    })()
  }, [])

  const openModal = React.useCallback((e, action, dataIndex) => {
    e.preventDefault()
    setIsModalOpen(true)
    setAction(action)
    setEditIndex(dataIndex)
  }, [])

  const closeModal = () => {
    setIsModalOpen(false)
  }
  const closeProfileDetailsModal = () => {
    setProfileDetailsModalOpen(false)
    setParentDetails([])
    setSelectedParentUuid(null)
    setSelectedParentImage('')
  }
  const onActionButtonClick = async (e, action, dataIndex) => {
    handleClose(dataIndex)
    openModal(e, action, dataIndex)
  }

  const onSaveClick = async (newData, action) => {
    try {
      const res = await membershipActions(newData, action)
      if (
        res?.success ||
        res?.subscription?.uuid ||
        res?.uuid ||
        res?.user_id
      ) {
        eventEmitter.emit(constants.CLOSE_MODAL_FORM)
        eventAlert('Done', constants.SUCCESS)
        closeModal()
        const queries = JSON.parse(localStorage.getItem('filterQueries'))
        dispatch(
          fetchParentData({ ...queries }, false, false, constants.PARENT)
        )
      }
    } catch (error) {
      console.log(error)
      eventEmitter.emit(constants.MODAL_FORM_SAVE_ERROR, error)
      eventAlert(error.message, constants.ERROR)
    }
  }

  const modifiedColumns = React.useMemo(() => {
    return [
      ...columns.slice(0, 6),
      // Multi-guardian: surface which family member each row represents.
      {
        ...tableColumnDefaultOption,
        name: '_relationship',
        label: 'Relationship',
        modalEdit: false,
        disabledInForm: true,
        options: {
          ...tableColumnDefaultOption.options,
          disableFilter: true,
          customBodyRenderLite: (dataIndex) => {
            const row = parentData.data[dataIndex] || {}
            return (
              <span>
                {row._is_primary ? '★ ' : ''}
                {row._relationship}
              </span>
            )
          }
        }
      },
      {
        ...tableColumnDefaultOption,
        name: 'user.state',
        label: 'State',
        widget: 'select',
        valueLabelMappings: states,
        defaultValue: () =>
          new URLSearchParams(window.location.search).get('state'),
        modalEdit: true,
        options: {
          ...tableColumnDefaultOption.options,
          filterKey: 'state',
          disableFilter: true,
          customBodyRender: (value) => <span>{states[value]}</span>,
          customBodyRenderLite: (dataIndex) => {
            const { state } = parentData.data[dataIndex]?.user || {}
            return <span>{states[state]}</span>
          }
        }
      },
      ...columns.slice(6),
      {
        ...commonAttributes.date,
        modalEdit: false,
        disabledInForm: true,
        name: 'user.active_subscription.current_cycle.end_date',
        label: 'Plan renewal date',
        options: {
          ...commonAttributes.date.options,
          filterKey: 'plan_renewal_date',
          fixedColumn: false,
          customBodyRenderLite: (dataIndex) => {
            const { active_subscription } =
              parentData?.data[dataIndex]?.user || {}
            return <span>{active_subscription?.current_cycle?.end_date}</span>
          }
        }
      },
      {
        modalEdit: false,
        disabledInForm: true,
        name: 'user.app_status',
        label: 'App status',
        options: {
          ...tableColumnDefaultOption.options,
          filterKey: 'app_status',
          fixedColumn: false
        }
      },
      {
        name: 'user.status',
        label: 'User status',
        widget: 'select',
        widgetFilter: true,
        isValueKey: true,
        modalEdit: false,
        disabledInForm: true,
        validate: validateNotEmpty,
        valueLabelMappings: user_statuses,
        options: {
          ...tableColumnDefaultOption.options,
          filterKey: 'user_status',
          customBodyRender: (value) => <span>{value ? value : null}</span>
        }
      },
      {
        name: 'actions',
        disabledInForm: true,
        options: {
          customBodyRenderLite: (dataIndex) => {
            const { user } = parentData.data[dataIndex]
            const status = user?.status

            const userActions = status ? parentActions : nonMemberActions

            return (
              <div key={dataIndex}>
                <Button
                  aria-controls={
                    Boolean(anchorEl[dataIndex])
                      ? 'demo-customized-menu'
                      : undefined
                  }
                  aria-haspopup="true"
                  aria-expanded={
                    Boolean(anchorEl[dataIndex]) ? 'true' : undefined
                  }
                  variant="contained"
                  onClick={() => profileButtonOnclickHandler(dataIndex)}
                  className="button-alfred"
                >
                  view profile details
                </Button>
                <Button
                  id={'Button' + dataIndex}
                  aria-controls={
                    Boolean(anchorEl[dataIndex])
                      ? 'demo-customized-menu'
                      : undefined
                  }
                  aria-haspopup="true"
                  aria-expanded={
                    Boolean(anchorEl[dataIndex]) ? 'true' : undefined
                  }
                  variant="contained"
                  onClick={(e) => handleClick(e, dataIndex)}
                  endIcon={<KeyboardArrowDownIcon />}
                  className="button-alfred"
                >
                  Options
                </Button>
                <Menu
                  id={'Menu' + dataIndex}
                  MenuListProps={{
                    'aria-labelledby': 'demo-customized-button'
                  }}
                  anchorEl={anchorEl[dataIndex]}
                  open={Boolean(anchorEl[dataIndex])}
                  onClose={() => handleClose(dataIndex)}
                >
                  {Object.keys(userActions)
                    .filter((el) =>
                      el === 'extend' && status === 'due' ? false : true
                    )
                    .map((action, index) => {
                      return (
                        <MenuItem
                          onClick={(e) => {
                            onActionButtonClick(e, action, dataIndex)
                          }}
                          disableRipple
                          key={index}
                        >
                          {userActions[action]}
                        </MenuItem>
                      )
                    })}
                </Menu>
              </div>
            )
          },
          disableFilter: true
        }
      }
    ]
  }, [states, parentData, anchorEl])

  return (
    <>
      <BaseEntity
        entity={constants.PARENT}
        label="Guardian"
        columns={modifiedColumns}
        data={parentData}
        fetchData={fetchParentData}
        updateEntityData={updateUserData}
        disableDelete
        modalCss={`
        .MuiDialogContent-root {
            overflow-y: unset;
        }
      `}
      />
      <MasterForm
        cartType="recharge"
        isModalOpen={isModalOpen}
        onSaveClick={onSaveClick}
        closeModal={closeModal}
        action={action}
        data={parentData?.data[editIndex]}
      />
      <ModalForm
        isModalOpen={profileDetailsModalOpen}
        onModalClose={closeProfileDetailsModal}
        enableSave={false}
      >
        <ParentDetails parentDetails={parentDetails} />
        {selectedParentUuid ? (
          <div style={{ marginTop: 24 }}>
            <ParentAvatarPanel
              parentUuid={selectedParentUuid}
              initialImage={selectedParentImage}
              parentName={parentDetails?.parentData?.parentName}
            />
          </div>
        ) : null}
        {/* Multi-guardian: lets admin add/edit/remove family member phones
            and set the primary outbound. Embedded inside the existing profile
            modal so the panel sits next to other parent context. */}
        {selectedParentUuid ? (
          <div style={{ marginTop: 24 }}>
            <GuardiansPanel parentUuid={selectedParentUuid} />
          </div>
        ) : null}
      </ModalForm>
    </>
  )
}

export default Parent
